import React, { useState } from 'react';

export default function HomeView({
  dashboard,
  projectsList,
  onSwitchProject,
  onDeleteProject,
  onCreateProject,
  onOpenOverview,
  terminology
}) {
  const school = dashboard.school;
  const activeProjectId = dashboard.project?.id;
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  function handleDeleteClick(event, projectId) {
    event.stopPropagation();
    setDeleteConfirmId(projectId);
  }

  function handleConfirmDelete(event) {
    event.stopPropagation();
    if (deleteConfirmId) {
      onDeleteProject(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  }

  function handleCancelDelete(event) {
    event.stopPropagation();
    setDeleteConfirmId(null);
  }

  return (
    <div className="home-screen">
      <section className="hero-card home-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">
              {dashboard.dataSource === 'database' ? 'Saved' : 'Demo'}
            </span>
            <span className="eyebrow-copy">{school.planLabel}</span>
          </div>
          <h1>{school.name}</h1>
          <p>
            Welcome to Smart Sort. Select a project below to manage {terminology.students.toLowerCase()}, define
            {terminology.classes.toLowerCase()}, and generate optimized {terminology.class.toLowerCase()} assignments.
          </p>
        </div>

        <div className="hero-actions">
          <button type="button" className="primary-button" onClick={onCreateProject}>
            + New project
          </button>
        </div>
      </section>

      <section className="home-stats-strip">
        <div className="metric-card">
          <span className="metric-label">Projects</span>
          <strong>{projectsList.length}</strong>
          <p className="metric-delta neutral">Across all school years</p>
        </div>
        <div className="metric-card">
          <span className="metric-label">Total {terminology.students.toLowerCase()}</span>
          <strong>{projectsList.reduce((sum, p) => sum + (p.studentCount ?? 0), 0)}</strong>
          <p className="metric-delta neutral">Across all projects</p>
        </div>
        <div className="metric-card">
          <span className="metric-label">Total {terminology.classes.toLowerCase()}</span>
          <strong>{projectsList.reduce((sum, p) => sum + (p.classCount ?? 0), 0)}</strong>
          <p className="metric-delta neutral">Across all projects</p>
        </div>
        <div className="metric-card">
          <span className="metric-label">Completed runs</span>
          <strong>{projectsList.reduce((sum, p) => sum + (p.runCount ?? 0), 0)}</strong>
          <p className="metric-delta neutral">Across all projects</p>
        </div>
      </section>

      <section className="panel home-projects-panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">Projects</span>
            <h2>All projects</h2>
          </div>
          <span className="toolbar-meta">{projectsList.length} projects</span>
        </div>

        {projectsList.length === 0 ? (
          <div className="loading-card">
            <h1>No projects yet</h1>
            <p>Create your first placement project to get started.</p>
            <button type="button" className="primary-button" onClick={onCreateProject}>
              + New project
            </button>
          </div>
        ) : (
          <div className="home-project-grid">
            {projectsList.map((project) => {
              const isActive = project.id === activeProjectId;
              const isConfirming = deleteConfirmId === project.id;
              return (
                <article
                  key={project.id}
                  className={`home-project-card ${isActive ? 'is-active' : ''}`}
                  onClick={() => {
                    if (!isConfirming) {
                      onSwitchProject(project.id);
                      onOpenOverview();
                    }
                  }}
                >
                  {isConfirming ? (
                    <div className="home-project-confirm-delete">
                      <p>Delete <strong>{project.name}</strong>?</p>
                      <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0.25rem 0 0.75rem' }}>
                        This will permanently remove all data in this project.
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          type="button"
                          className="utility-button"
                          onClick={handleCancelDelete}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="primary-button danger-button"
                          onClick={handleConfirmDelete}
                          style={{ fontSize: '0.8rem' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="home-project-card-head">
                        <strong>{project.name}</strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          {isActive && <span className="tone-pill accent">Active</span>}
                          <button
                            type="button"
                            className="home-project-delete-btn"
                            onClick={(e) => handleDeleteClick(e, project.id)}
                            aria-label={`Delete ${project.name}`}
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                      <p className="home-project-meta">
                        {project.schoolYear}
                        {project.sourceGrade ? ` · Grade ${project.sourceGrade}` : ''}
                        {project.targetGrade ? ` → ${project.targetGrade}` : ''}
                      </p>
                      <div className="home-project-stats">
                        <span>{project.studentCount ?? 0} {terminology.students.toLowerCase()}</span>
                        <span>{project.classCount ?? 0} {terminology.classes.toLowerCase()}</span>
                        <span>{project.runCount ?? 0} runs</span>
                      </div>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
